import React, { useEffect, useRef, useContext } from 'react';
import classNames from 'classnames/bind';
import MicIcon from '@mui/icons-material/Mic';
import { Button } from 'gd-design-library';
import { SnackbarContext } from '@utils';
import styles from './VoiceInputButton.module.scss';

const cx = classNames.bind(styles);
const CN = 'voice-input-button';

interface WindowWithSpeechRecognition extends Window {
  SpeechRecognition?: new () => SpeechRecognition;
  webkitSpeechRecognition?: new () => SpeechRecognition;
}

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  isListening: boolean;
  onListeningChange: (isListening: boolean) => void;
  disabled?: boolean;
}

export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onTranscript,
  isListening,
  onListeningChange,
  disabled = false
}) => {
  const { setSnackbarParams } = useContext(SnackbarContext);

  const SpeechRecognition =
    typeof window !== 'undefined' &&
    ((window as WindowWithSpeechRecognition).SpeechRecognition ||
    (window as WindowWithSpeechRecognition).webkitSpeechRecognition);

  const isSpeechSupported = Boolean(SpeechRecognition);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const showError = (message: string): void => {
    setSnackbarParams({
      message,
      severity: 'warning',
      open: true,
      onClose: () => setSnackbarParams({ open: false, message: '', onClose: () => null })
    });
  };

  useEffect(() => {
    if (!isSpeechSupported) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onend = (): void => {
      onListeningChange(false);
    };

    recognition.onerror = (event): void => {
      // eslint-disable-next-line no-console
      console.error('Speech recognition error:', event.error);
      onListeningChange(false);

      showError(
        event.error === 'not-allowed'
          ? 'Microphone access was denied.'
          : 'Voice recognition error. Please try again.'
      );
    };

    recognition.onresult = (event): void => {
      let interim = '';
      let finalTranscript = '';

      Array.from(event.results).forEach(result => {
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      });

      if (finalTranscript) {
        onTranscript(finalTranscript.trim());
      } else if (interim) {
        onTranscript(interim);
      }
    };

    recognitionRef.current = recognition;

    return (): void => {
      const recognitionInstance = recognitionRef.current;
      if (!recognitionInstance) return;

      try {
        recognitionInstance.stop();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to stop recognition:', error);
      }
    };
  }, [ isSpeechSupported, onTranscript, onListeningChange ]);

  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    if (isListening) {
      try {
        recognition.start();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start recognition:', error);
        onListeningChange(false);
        showError('Unable to start voice input.');
      }
    } else {
      recognition.stop();
    }
  }, [ isListening, onListeningChange ]);

  const handleVoiceInputClick = (): void => {
    onListeningChange(!isListening);
  };

  if (!isSpeechSupported) {
    return null;
  }

  return (
    <Button
      variant='inherit'
      isIcon
      className={ cx(CN, { active: isListening }) }
      onClick={ handleVoiceInputClick }
      disabled={ disabled }
    >
      <MicIcon className={ cx(`${CN}__icon`) } />
    </Button>
  );
};
